import React, { useState } from 'react';
//import moment from 'moment';
import MuiTableCell from '@mui/material/TableCell';
import { withStyles, makeStyles } from '@mui/styles';
import axios from 'axios';
import {
  Box,
  Table,
  TableRow,
  TableBody,
  Tooltip,
  TableContainer,
  TablePagination
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';

import { printPrettyPrice } from '@/common/utils/utils';
import AutoPayDetailsModal from './Modal/AutoPayDetail';
import { EnhancedTableHead } from '@/common/utils/DataTableHelper';
import {
  TableLoader,
  TableNoDataPlaceholder
} from '../../../../../../components/TableLoader';
import { Retail2ContextProvider } from '../../../../../../context/Retail2Context';
import moment from 'moment';
import { getRestHeaders } from '@/common/utils/api';
import CONFIG from '@/config';

const TableCell = withStyles({
  root: {
    borderBottom: '1px solid #F4F6F8'
  }
})(MuiTableCell);

const useStyles: any = makeStyles(theme => ({
  root: {
    width: '100%'
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2)
  },
  table: {
    minWidth: 750
  },
  cell: {
    whiteSpace: 'nowrap',
    maxWidth: 80,
    padding: '5px 10px',
    textOverflow: 'ellipsis',
    overflow: 'hidden'
  },
  cellSm: {
    whiteSpace: 'nowrap',
    maxWidth: 60,
    textOverflow: 'ellipsis',
    overflow: 'hidden'
  },
  cellLg: {
    whiteSpace: 'nowrap',
    maxWidth: 250,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    height: 50
  },
  visuallyHidden: {
    top: 20,
    width: 1,
    border: 0,
    height: 1,
    margin: -1,
    padding: 0,
    overflow: 'hidden',
    position: 'absolute',
    clip: 'rect(0 0 0 0)'
  },
  loader: {
    position: 'absolute',
    width: '100%',
    height: '78vh',
    backgroundColor: 'white',
    opacity: 0.8,
    verticalAlign: 'middle',
    padding: '10% 30%',
    zIndex: 3
  }
}));

const columnConfig = [
  {
    id: 'description',
    label: 'Membership'
  },
  {
    id: 'scheduled_bill_date',
    label: 'Scheduled Bill Date'
  },
  {
    id: 'payment_date',
    label: 'Bill Date'
  },
  {
    id: 'last4',
    label: 'Credit Card'
  },
  {
    id: 'amount',
    label: 'Amount'
  },
  {
    id: 'status',
    label: 'Status'
  },
  {
    id: 'action',
    label: 'Action',
    sortable: false
  }
];

const formatDateCell = val =>
  val ? moment(val).utc().format('MM/DD/YYYY') : '--';

const AutoPays = props => {
  const { customerAllData } = props;
  const classes = useStyles();
  const [page, setPage] = React.useState(0);
  const [order, setOrder] = React.useState('desc');
  const [orderBy, setOrderBy] = React.useState('scheduled_bill_date');
  const [selectedAutoPay, setSelectedAutoPay] = useState(null);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [allAutoPay, setAllAutoPay] = useState([]);
  const [isAutoPayDetailModalOpen, setIsAutoPayDetailModalOpen] =
    useState(false);
  const [autoPaysLoading, setAutoPaysLoading] = React.useState(true);

  const has_autopay_success_status = status_code => {
    // Successfully charged or prepaid month
    if (status_code == 0 || status_code == 2) {
      return true;
    }
    return false;
  };

  // Rest API Autopay function 'customers_autopay_history'
  async function getAutoPays() {
    setAutoPaysLoading(true);

    // Construct our full endpiont
    const url = CONFIG.API + `/pos_customer/customers_autopay_history/`;

    // Call to the backend REST API and handle success and failures using the promises
    await axios
      .get(url, {
        params: {
          customer_id: customerAllData?.id,
          sortby: orderBy,
          direction: order
        },
        headers: getRestHeaders()
      })
      .then(res => {
        setAllAutoPay(res?.data || []);
        setAutoPaysLoading(false);
      })
      .catch(e => console.error(e, 'error'));
  }

  React.useEffect(() => {
    if (customerAllData?.id) {
      getAutoPays();
    }
  }, [customerAllData, order, orderBy]);

  const handleAutoPayDetailModal = id => {
    const currentAutoPay = allAutoPay && allAutoPay.find(p => p.id === id);

    setSelectedAutoPay(currentAutoPay);
    setIsAutoPayDetailModalOpen(true);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, allAutoPay.length - page * rowsPerPage);

  return (
    <>
      <TableContainer>
        {autoPaysLoading && (
          <Box className={classes.loader}>
            <TableLoader />
          </Box>
        )}
        <Table className={classes.table}>
          <EnhancedTableHead
            order={order}
            orderBy={orderBy}
            classes={classes}
            TABLE_HEAD={columnConfig}
            rowCount={allAutoPay.length}
            onRequestSort={handleRequestSort}
          />
          {allAutoPay.length > 0 ? (
            <TableBody>
              {allAutoPay
                ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map(row => {
                  return (
                    <TableRow>
                      <TableCell
                        className={classes.cellLg}
                        title={row.description}
                      >
                        <div
                          style={{
                            whiteSpace: 'normal'
                          }}
                        >
                          {row.description}
                        </div>
                      </TableCell>
                      <TableCell className={classes.cellSm}>
                        {formatDateCell(row.scheduled_bill_date)}
                      </TableCell>
                      <TableCell className={classes.cellSm}>
                        {formatDateCell(row.payment_date)}
                      </TableCell>
                      <TableCell className={classes.cellSm}>
                        {row.last4 && row.amount > 0
                          ? row.card_issuer + ' **' + row.last4
                          : '--'}
                      </TableCell>
                      <TableCell className={classes.cellSm}>
                        {row.amount > 0 ? printPrettyPrice(row.amount) : '--'}
                      </TableCell>
                      <TableCell className={classes.cellLg}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start'
                          }}
                        >
                          {has_autopay_success_status(row.status_code) ? (
                            <CheckCircleIcon
                              style={{ color: 'green', fontSize: 'large' }}
                            />
                          ) : (
                            <ErrorIcon
                              style={{ color: 'red', fontSize: 'large' }}
                            />
                          )}
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              flexWrap: 'wrap',
                              whiteSpace: 'normal',
                              marginLeft: '8px'
                            }}
                          >
                            {row.status}
                            {!has_autopay_success_status(row.status_code)
                              ? ' - ' + row.message
                              : ''}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className={classes.cellSm}>
                        {row.retry ? (
                          <Tooltip arrow title="Run AutoPay Again">
                            <ReplayOutlinedIcon
                              style={{ color: '#57b8eb', fontSize: 'large' }}
                              onClick={() => handleAutoPayDetailModal(row.id)}
                            />
                          </Tooltip>
                        ) : (
                          <ReplayOutlinedIcon
                            style={{ color: '#c2c2c2', fontSize: 'large' }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          ) : (
            <TableNoDataPlaceholder />
          )}
        </Table>
      </TableContainer>
      <Box sx={{ position: 'relative' }}>
        <TablePagination
          page={page}
          component="div"
          count={allAutoPay.length}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          rowsPerPageOptions={[10, 50, 100]}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
      {selectedAutoPay && (
        <Retail2ContextProvider>
          <AutoPayDetailsModal
            autoPay={selectedAutoPay}
            open={isAutoPayDetailModalOpen}
            customerAllData={customerAllData}
            setModal={setIsAutoPayDetailModalOpen}
            getAutoPays={getAutoPays}
          />
        </Retail2ContextProvider>
      )}
    </>
  );
};

export default AutoPays;
